import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const { register } = useAuth()

  const validateForm = () => {
    const newErrors = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setLoading(true)
    
    const result = await register(formData.email, formData.password)
    
    if (!result.success) {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
    
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors(prev => ({
        ...prev,
        [e.target.name]: ''
      }))
    }
  }

  return (
    <div className="mx-auto max-w-md px-4 sm:px-6 py-12 lg:py-20">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-accent-500 to-accent-700 shadow-glow-accent flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
        </div>
        <h2 className="mt-4 font-display font-semibold text-2xl text-ink-900 tracking-tight">Create your account</h2>
        <p className="mt-1.5 text-sm text-ink-500">Bring your portfolio into one disciplined view</p>
      </div>

      {/* Form */}
      <div className="card shadow-elev-3">
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink-700 mb-1">Email address</label>
            <input
              id="email" name="email" type="email" autoComplete="email" required
              value={formData.email} onChange={handleChange}
              className={`input ${errors.email ? 'border-loss-400' : ''}`}
              placeholder="you@example.com"
            />
            {errors.email && <p className="mt-1 text-sm text-loss-600">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-ink-700 mb-1">Password</label>
            <div className="relative">
              <input
                id="password" name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password" required
                value={formData.password} onChange={handleChange}
                className={`input pr-10 ${errors.password ? 'border-loss-400' : ''}`}
                placeholder="Create a password"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-400 hover:text-ink-600" aria-label="Toggle password">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-sm text-loss-600">{errors.password}</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink-700 mb-1">Confirm password</label>
            <div className="relative">
              <input
                id="confirmPassword" name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password" required
                value={formData.confirmPassword} onChange={handleChange}
                className={`input pr-10 ${errors.confirmPassword ? 'border-loss-400' : ''}`}
                placeholder="Confirm your password"
              />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-400 hover:text-ink-600" aria-label="Toggle password">
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && <p className="mt-1 text-sm text-loss-600">{errors.confirmPassword}</p>}
          </div>

          <button type="submit" disabled={loading} className="w-full btn btn-primary flex items-center justify-center">
            {loading ? <LoadingSpinner size="small" /> : 'Create account'}
          </button>

          <p className="text-center text-sm text-ink-500">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-accent-700 hover:text-accent-800">Sign in</Link>
          </p>
        </form>
      </div>

      <p className="mt-5 text-center text-xs text-ink-400">
        By creating an account, you agree to our{' '}
        <Link to="/terms" className="text-ink-500 hover:text-accent-700 underline">Terms</Link> and{' '}
        <Link to="/privacy" className="text-ink-500 hover:text-accent-700 underline">Privacy</Link>.
      </p>
    </div>
  )
}

export default Register